import React, {useEffect, useState} from "react";
import {fileToBase64} from "../../helper/tools";
import imageCompression from "browser-image-compression";
import useAuth from "../../hooks/useAuth";
import jwt_decode from "jwt-decode";
import {getMediaInfos, uploadAgencyMedia} from "../../api/pro/galleryMediaSiteReq";

export default function FormEditMedium({apiGalleryContent, apiGalleryHeaderList, mediaId, legend, categoryId, headerId, setShowModal}) {

    const [selectedFile, setSelectedFile] = useState(null);
    const [fileName, setFileName] = useState("");
    const [description, setDescription] = useState("");
    const [salesInfos, setSalesInfos] = useState("");
    const [mediaIdValue, setMediaIdValue] = useState("0");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [preview, setPreview] = useState(null);
    const [originalSize, setOriginalSize] = useState(0);
    const [compressedSize, setCompressedSize] = useState(0);

    const { auth } = useAuth();
    const token = auth?.token;
    const decodedToken = jwt_decode(token);
    const agencyId = decodedToken.agency;

    useEffect(() => {
        if (mediaId && mediaId !== "0") {
            const fetchMediaInfos = async () => {
                const response = await getMediaInfos(token, mediaId);

                if (response) {
                    const mediaData = response?.data || response;

                    if (mediaData) {
                        setDescription(mediaData.description || "");
                        setSalesInfos(mediaData.sales_infos || "");
                        setMediaIdValue(mediaId);

                        if (mediaData.name) {
                            const IMG_FOLDER = process.env.REACT_APP_LOCAL_ASSETS_BASE_URL;
                            const mediaUrl = `${IMG_FOLDER}/media/galery/${agencyId}/${mediaData.name}`;
                            setPreview(mediaUrl);
                            setFileName(mediaData.name || "média existant");
                        }
                    }
                }
            };
            fetchMediaInfos();
        }
    }, [mediaId, token]);

    /**
     * Compression d'image avec browser-image-compression
     */
    const compressImage = async (file) => {
        const options = {
            maxSizeMB: 1,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
            initialQuality: 0.85,
            fileType: file.type
        };

        const compressedFile = await imageCompression(file, options);
        if (compressedFile) {
            return compressedFile;
        } else {
            setError("Erreur lors de la compression de l'image");
            return null;
        }
    };

    /**
     * Validation et limitation de taille pour les vidéos
     * La compression vidéo se fait côté serveur
     */
    const validateVideo = async (file) => {
        const maxSize = 100 * 1024 * 1024;

        if (file.size > maxSize) {
            setError("La vidéo est trop volumineuse (max 100MB)");
            return null;
        }

        const acceptedFormats = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime'];
        if (!acceptedFormats.includes(file.type)) {
            setError("Format vidéo non supporté. Utilisez MP4, WebM, OGG ou MOV");
            return null;
        }

        return file;
    };

    /**
     * Gestion du changement de fichier
     */
    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setError("");
        setIsLoading(true);
        setOriginalSize(file.size);

        try {
            let processedFile = file;

            if (file.type.startsWith('image/')) {
                processedFile = await compressImage(file);
                setCompressedSize(processedFile.size);
            } else if (file.type.startsWith('video/')) {
                processedFile = await validateVideo(file);
                setCompressedSize(processedFile.size);
            } else {
                throw new Error("Format non supporté. Utilisez des images (JPG, PNG, WebP) ou des vidéos (MP4, WebM, MOV).");
            }

            const previewUrl = URL.createObjectURL(processedFile);
            setPreview(previewUrl);

            setSelectedFile(processedFile);
            setFileName(file.name);
        } catch (err) {
            setError(err.message);
            setSelectedFile(null);
            setPreview(null);
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Soumission du formulaire
     */
    const handleSubmit = async (e) => {
        e.preventDefault();

        if ((!mediaId || mediaId === "0") && !selectedFile) {
            setError("Veuillez sélectionner un fichier");
            return;
        }

        setIsLoading(true);
        setError("");

        const formData = {
            agencyId: agencyId,
            categoryId: categoryId || "1",
            file: "undefined",
            file_name: fileName ? fileName : "undefined",
            description: description,
            file_type: "media",
            header_id: headerId !== undefined && headerId !== null ? headerId : "0",
            mediaId: mediaIdValue,
            sales_infos: salesInfos
        };

        if (selectedFile) {
            const base64File = await fileToBase64(selectedFile);
            formData.file = base64File;
            formData.file_name = fileName;
        }

        const response = await uploadAgencyMedia(token, formData);

        if (response) {
            const isSuccess = response?.message === "Uploaded successfully" ||
                             response?.message === "Media infos changed successfully" ||
                             response?.data?.message === "Media infos changed successfully";

            if (isSuccess) {
                if (setShowModal) {
                    apiGalleryContent();
                    apiGalleryHeaderList();
                    setShowModal(false);
                }
            } else {
                setError("Erreur lors de l'envoi: " + (response?.message || "Erreur inconnue"));
            }
        } else {
            setError("Erreur lors de l'envoi: Pas de réponse du serveur");
        }

        setIsLoading(false);
    };

    useEffect(() => {
        return () => {
            if (preview && preview.startsWith('blob:')) {
                URL.revokeObjectURL(preview);
            }
        };
    }, [preview]);

    /**
     * Formater la taille du fichier
     */
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    return (
        <div className="form-edit-medium-container">
            <form onSubmit={handleSubmit} className="form-edit-medium">
                <h2>{mediaId && mediaId !== "0" ? "Modifier le média" : "Ajouter un média"}</h2>

                {/* Input fichier */}
                <label htmlFor="file-input" className="form-label">
                    {mediaId && mediaId !== "0" ? "Remplacer le fichier (optionnel)" : "Fichier (Image ou Vidéo) *"}
                </label>
                <input
                    id="file-input"
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,video/mp4,video/webm,video/ogg,video/quicktime"
                    onChange={handleFileChange}
                    disabled={isLoading}
                    className="form-control"
                    required={!mediaId || mediaId === "0"}
                />
                {fileName && (
                    <div className="file-info">
                        <p className="file-name">📁 {fileName}</p>
                        {originalSize > 0 && (
                            <p className="file-size">
                                Taille originale: {formatFileSize(originalSize)}
                                {compressedSize !== originalSize && compressedSize > 0 && (
                                    <span className="compressed-info">
                                        {" → Compressé: "}{formatFileSize(compressedSize)}
                                        <span className="savings">
                                            {" "}(-{Math.round((1 - compressedSize / originalSize) * 100)}%)
                                        </span>
                                    </span>
                                )}
                            </p>
                        )}
                    </div>
                )}

                {/* Preview */}
                {preview && (
                    <div className="preview-container">
                        <label className="form-label">Aperçu</label>
                        {(() => {
                            const isImage = selectedFile?.type.startsWith('image/') ||
                                          (!selectedFile && preview && (preview.match(/\.(jpg|jpeg|png|gif|webp)(\?|$)/i) || preview.includes('image')));

                            return isImage ? (
                                <img src={preview} alt="Preview" className="preview-image" />
                            ) : (
                                <video src={preview} controls className="preview-video" />
                            );
                        })()}
                    </div>
                )}

                {/* Description */}
                <div className="form-input">
                    <label htmlFor="description" className="form-label">
                        Description
                    </label>
                    <input
                        id="description"
                        type="text"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder=""
                        className="form-control"
                    />
                </div>

                {/* Infos de vente */}
                <div className="form-input">
                    <label htmlFor="sales-infos" className="form-label">
                        Infos de vente
                    </label>
                    <input
                        id="sales-infos"
                        type="text"
                        value={salesInfos}
                        onChange={(e) => setSalesInfos(e.target.value)}
                        placeholder=""
                        className="form-control"
                    />
                </div>

                {/* Media ID (caché pour nouveau média) */}
                {mediaId && mediaId !== "0" && (
                    <div className="form-input">
                        <label htmlFor="media-id" className="form-label">
                            Media ID
                        </label>
                        <input
                            id="media-id"
                            type="text"
                            value={mediaIdValue}
                            readOnly
                            className="form-control"
                            disabled
                        />
                    </div>
                )}

                {/* Message d'erreur */}
                {error && (
                    <div className="error-message">
                        ⚠️ {error}
                    </div>
                )}

                {/* Boutons d'action */}
                <div className="form-actions">
                    <button
                        type="submit"
                        disabled={isLoading || ((!mediaId || mediaId === "0") && !selectedFile)}
                        className="btn green-button"
                    >
                        <i className="fa fa-check"></i>
                        <span>{isLoading ? "Envoi en cours..." : "Envoyer"}</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setShowModal && setShowModal(false)}
                        disabled={isLoading}
                        className="btn red-button"
                    >
                        <i className="fa fa-xmark"></i>
                        <span>Annuler</span>
                    </button>
                </div>

                {/* Informations d'aide */}
                <div className="help-text">
                    <p><strong>Formats acceptés:</strong></p>
                    <ul>
                        <li>Images: JPG, PNG, WebP, GIF (max 1920px, compression automatique)</li>
                        <li>Vidéos: MP4, WebM, OGG, MOV (max 100MB)</li>
                    </ul>
                </div>
            </form>
        </div>
    );
}
